import React, { useEffect, useMemo, useState } from 'react';
import { Check, LayoutGrid, Target, Wrench } from 'lucide-react';
import { VERB_LABELS } from '@paradigm/permissions/constants/formA';
import { NodeMark, Glyphed } from './faces';
import { filterWords } from '../../nucleus/filters';
import { usePluginNames, toolNameOf, onlyPluginsLine } from '../../pages/plugins/data/elsewhere';
import { accessWord } from '../../pages/plugins/data/words';

// WHAT A SET OF PERMISSIONS ALLOWS, in the consent page's own words and parts:
// one row per schema with its allowed verbs as the consent page's tags (read,
// edit, create, delete, propose), then its Tools, then "Other access" in the
// sentences the consent page uses ("See your friends"). The same component draws
// a realm's ceiling and a contract's grant, so the two read the same way.
// TOOLS (plugins.execute) are said one per tool, by plugin and tool name, with
// its tool access ("Mailroom · Send email", Asks you); one that names no tool says
// which plugins it reaches ("Only Mailroom"). `toolDoor(pluginId)` makes each
// tool a door to that plugin's Tool access sheet; `tools` always says the group
// ("No tools" when it has none), for an app's or agent's contract.
//
// `access` is the backend's shape: { resource: { verb: scope } }, where a verb
// that is present is allowed, a scope of {} is unrestricted, and a scope may
// name schemas ({ id: { fields, filter } }) or specific nodes.

const NODE_VERBS = ['read', 'edit', 'create', 'delete', 'propose'];
const asScope = (s) => (s && typeof s === 'object' ? s : {});

/** The rows: [{ key, label, verbs, fields, filter, nodes }], its tools
 *  ({ tools: [{ pluginId, toolId, mode }], pluginIds } or null) and the other access. */
export function accessLinesOf(access = {}, schemaNames = {}) {
    const nodes = asScope(access.nodes);
    // schema → { verbs, fields, filter }, keyed by the schema's NAME when it is
    // known: each version of a schema has its own id, and they read as one schema
    const bySchema = new Map();
    const all = new Set();          // verbs over every kind of data
    const specific = new Map();     // node id → verbs
    for (const [verbRaw, scopeRaw] of Object.entries(nodes)) {
        const verb = verbRaw === 'edit_content' ? 'edit' : verbRaw;
        if (!NODE_VERBS.includes(verb)) continue;
        const scope = asScope(scopeRaw);
        const schemas = scope.schemas;
        const ids = Array.isArray(schemas) ? schemas.map((id) => [id, {}])
            : schemas && typeof schemas === 'object' ? Object.entries(schemas) : [];
        const named = Array.isArray(scope.nodes) ? scope.nodes : [];
        if (!ids.length && !named.length) { all.add(verb); continue; }
        for (const [id, mask] of ids) {
            const key = schemaNames[id] ? `name:${schemaNames[id]}` : String(id);
            const row = bySchema.get(key) || { id: String(id), verbs: new Set(), fields: null, filter: null };
            row.verbs.add(verb);
            const m = asScope(mask);
            if (Array.isArray(m.fields)) row.fields = m.fields;
            if (m.filter && Object.keys(m.filter).length) row.filter = m.filter;
            bySchema.set(key, row);
        }
        for (const id of named) {
            const s = specific.get(String(id)) || new Set();
            s.add(verb);
            specific.set(String(id), s);
        }
    }
    const data = [];
    if (all.size) data.push({ key: 'all', label: 'All nodes', verbs: [...all] });
    for (const row of [...bySchema.values()].sort((a, b) => String(schemaNames[a.id] || a.id).localeCompare(String(schemaNames[b.id] || b.id)))) {
        data.push({ key: row.id, label: schemaNames[row.id] || 'A schema', verbs: [...row.verbs], fields: row.fields, filter: row.filter });
    }
    if (specific.size) {
        const verbs = new Set([...specific.values()].flatMap((s) => [...s]));
        data.push({ key: 'nodes', label: specific.size === 1 ? '1 specific node' : `${specific.size} specific nodes`, verbs: [...verbs], nodes: [...specific.keys()] });
    }

    // links always come with what a realm lets be read, so they get no line of their own
    const other = [];
    let tools = null;
    for (const [res, verbs] of Object.entries(access)) {
        if (res === 'nodes' || !verbs || typeof verbs !== 'object') continue;
        for (const [verb, scopeRaw] of Object.entries(verbs)) {
            const scope = asScope(scopeRaw);
            if (res === 'plugins' && verb === 'execute') {
                tools = { tools: toolLimits(scope.tools), pluginIds: Array.isArray(scope.plugin_ids) ? scope.plugin_ids.map(String) : [] };
                continue;
            }
            other.push({ key: `${res}.${verb}`, text: VERB_LABELS[`${res}.${verb}`] || `${res} · ${verb}` });
        }
    }
    return { data, other, tools };
}

// every tool a mode is set for, by plugin: Allowed first, then Asks you, then Never
const MODE_ORDER = { allow: 0, ask: 1, deny: 2 };
function toolLimits(raw) {
    if (!raw || typeof raw !== 'object') return [];
    const out = [];
    for (const [pluginId, modes] of Object.entries(raw)) {
        if (!modes || typeof modes !== 'object') continue;
        for (const [toolId, mode] of Object.entries(modes)) if (mode in MODE_ORDER) out.push({ pluginId, toolId, mode });
    }
    return out.sort((a, b) => MODE_ORDER[a.mode] - MODE_ORDER[b.mode]);
}

// The titles of the specific nodes a blob names (the first two dozen), read in
// one call through `loadTitles(ids)` → { id: title }.
function useNodeTitles(data, loadTitles) {
    const ids = useMemo(() => data.flatMap((l) => l.nodes || []).slice(0, 24), [data]);
    const key = ids.join(',');
    const [titles, setTitles] = useState({});
    useEffect(() => {
        if (!ids.length || !loadTitles) return undefined;
        let live = true;
        loadTitles(ids).then((t) => { if (live) setTitles(t || {}); }).catch(() => {});
        return () => { live = false; };
    }, [key, loadTitles]); // eslint-disable-line react-hooks/exhaustive-deps
    return titles;
}

// each row's mark, as the identity graph draws a schema: its own icon on a square
const markOf = (l) => (l.key === 'all' ? <Glyphed size={22}><LayoutGrid size={11} strokeWidth={1.8} /></Glyphed>
    : l.key === 'nodes' ? <Glyphed size={22}><Target size={11} strokeWidth={1.8} /></Glyphed>
        : <NodeMark schemaId={l.key} schema={l.label} size={22} />);

/** The rows, drawn in the consent page's grammar (components/grant parts.css): one
 *  row per schema with its allowed verbs as tags, then "Other access". Drawn
 *  inside a ConsentFrame, so the rows read as they do on the consent page. */
export default function AccessList({ access, schemaNames, loadTitles, empty = 'Nothing.', tools: always = false, toolDoor = null }) {
    const { data, other, tools } = useMemo(() => accessLinesOf(access || {}, schemaNames || {}), [access, schemaNames]);
    const nodeTitles = useNodeTitles(data, loadTitles);
    if (!data.length && !other.length && !tools && !always) return <p className="gp-bound__none">{empty}</p>;
    return (
        <div className="nx-rules">
            {data.length > 0 && <div className="gp-rcpt__grp">Nodes by schema</div>}
            {data.length > 0 && (
                <div className="gp-bound">
                    {data.map((l) => (
                        <div key={l.key} className="nx-rules__row">
                            <div className="gp-brow is-lit">
                                <span className="nx-rules__icon">{markOf(l)}</span>
                                <span className="gp-brow__lab">{l.label}</span>
                                <span className="gp-brow__verbs">
                                    {NODE_VERBS.filter((v) => l.verbs.includes(v)).map((v) => <em key={v} className={`gp-vtag is-${v}`}>{v}</em>)}
                                </span>
                            </div>
                            {l.key === 'all' && <p className="nx-rules__more">Every schema you have, and any you add later.</p>}
                            {l.fields && <p className="nx-rules__more">Only {l.fields.join(', ')}</p>}
                            {l.filter && <p className="nx-rules__more">Only where {filterWords(l.filter)}</p>}
                            {l.nodes && <p className="nx-rules__more">{l.nodes.slice(0, 12).map((id) => nodeTitles[id] || 'Untitled').join(' · ')}{l.nodes.length > 12 ? ` · and ${l.nodes.length - 12} more` : ''}</p>}
                        </div>
                    ))}
                </div>
            )}
            <ToolRows tools={tools} always={always} toolDoor={toolDoor} />
            <OtherRows other={other} />
        </div>
    );
}

const toolMark = <Glyphed size={22}><Wrench size={11} strokeWidth={1.8} aria-hidden="true" /></Glyphed>;

/** Tools: one row per tool by plugin and tool name, with its tool access; a door
 *  to its plugin's Tool access sheet when `toolDoor` is given. */
export function ToolRows({ tools, always = false, toolDoor = null }) {
    // the plugins and tools it names, by name, read only when it names some
    const names = usePluginNames(!!(tools?.tools?.length || tools?.pluginIds?.length));
    if (!tools && !always) return null;
    const list = tools?.tools || [];
    let none = null;
    if (!list.length) {
        if (!tools) none = 'No tools';
        else if (tools.pluginIds.length) none = `Tools of ${onlyPluginsLine(names, tools.pluginIds).replace(/^Only /, 'only ')}`;
        else none = 'Tools of every plugin';
    }
    return (<>
        <div className="gp-rcpt__grp">Tools</div>
        <div className="gp-bound">
            {list.map((t) => {
                const name = toolNameOf(names, t.pluginId, t.toolId);
                return (
                    <div key={`${t.pluginId}.${t.toolId}`} className="nx-rules__row">
                        <div className="gp-brow is-lit">
                            <span className="nx-rules__icon">{toolMark}</span>
                            <span className="gp-brow__lab">
                                {toolDoor
                                    ? <button type="button" className="nx-rules__door" onClick={() => toolDoor(t.pluginId)} aria-label={`${name}: tool access`}>{name}</button>
                                    : name}
                            </span>
                            <span className={`nx-rules__mode is-${t.mode}`}>{accessWord(t.mode)}</span>
                        </div>
                    </div>
                );
            })}
            {none && (
                <div className="nx-rules__row">
                    <div className={`gp-brow${tools ? ' is-lit' : ''}`}>
                        <span className="nx-rules__icon">{toolMark}</span>
                        <span className="gp-brow__lab">{none}</span>
                    </div>
                </div>
            )}
        </div>
    </>);
}

function OtherRows({ other }) {
    if (!other.length) return null;
    return (<>
        <div className="gp-rcpt__grp">Other access</div>
        <div className="gp-bound">
            {other.map((c) => (
                <div key={c.key} className="nx-rules__row">
                    <div className="gp-brow is-lit">
                        <span className="nx-rules__icon"><Glyphed size={22}><Check size={12} strokeWidth={2.2} aria-hidden="true" /></Glyphed></span>
                        <span className="gp-brow__lab is-cap">{c.text}</span>
                    </div>
                </div>
            ))}
        </div>
    </>);
}

/** Only what is not a node: its tools, the activity log, automations. The
 *  Preview draws it under the identity graph. */
export function OtherAccess({ access }) {
    const { other, tools } = useMemo(() => accessLinesOf(access || {}), [access]);
    return (<><ToolRows tools={tools} /><OtherRows other={other} /></>);
}
